import { c as _c } from "react/compiler-runtime";
import { type RefObject, useCallback, useLayoutEffect } from "react";
const STORAGE_KEY = "shop-scroll-restore";

/** `itemId` is the tile the photo transition lands on; `top` or `category` says where to scroll. */
type SavedSpot = {
  url: string;
  itemId: string;
  top?: number;
  category?: string;
};
function normalizeUrl(url: string) {
  const parsed = new URL(url, window.location.origin);
  parsed.searchParams.sort();
  return parsed.pathname + parsed.search;
}
function currentUrl() {
  return normalizeUrl(window.location.href);
}
function save(spot: SavedSpot): boolean {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(spot));
    return true;
  } catch {
    return false;
  }
}
function readSaved(): SavedSpot | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) as SavedSpot : null;
  } catch {
    return null;
  }
}
function clearSaved() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear when storage is blocked.
  }
}

/** Only boxes the shopper can scroll: never an overflow-hidden wrapper, which has no scrollbar to undo it. */
function isScrollContainer(node: Element) {
  if (node === document.scrollingElement) return true;
  const {
    overflowY
  } = getComputedStyle(node);
  return (overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight;
}

/** Scrolls whichever containers scroll (an inner panel on desktop, the page on phones) until `element` sits `top` px from the viewport top. */
function placeAt(element: Element, top: number) {
  let delta = element.getBoundingClientRect().top - top;
  for (let node = element.parentElement; node && Math.abs(delta) >= 1; node = node.parentElement) {
    if (!isScrollContainer(node)) continue;
    const before = node.scrollTop;
    node.scrollTop = before + delta;
    delta -= node.scrollTop - before;
  }
}

/** Breadcrumbs open the shop with scroll={false}; with nothing to restore, start at the top rather than the product page's offset. */
function scrollToTop() {
  document.scrollingElement?.scrollTo({
    top: 0
  });
}

/** The visible top and height of the box `element` scrolls in. */
function scrollPanelBounds(element: Element) {
  for (let node = element.parentElement; node; node = node.parentElement) {
    if (node !== document.scrollingElement && isScrollContainer(node)) {
      const rect = node.getBoundingClientRect();
      const top = Math.max(rect.top, 0);
      return {
        top,
        height: Math.min(rect.bottom, window.innerHeight) - top
      };
    }
  }
  return {
    top: 0,
    height: window.innerHeight
  };
}

/** The Storefront product page's breadcrumbs call this before opening the shop; false means nothing could be saved, so nothing will restore. */
export function rememberShopReturn(url: string, itemId: string, category?: string): boolean {
  const target = normalizeUrl(url);
  const saved = readSaved();
  if (!category && saved?.url === target && saved.itemId === itemId && saved.top !== undefined) {
    return true;
  }
  return save({
    url: target,
    itemId,
    category
  });
}

/** Whether the shop is being reopened onto this tile, so its photo loads eagerly and the transition can wait for it. */
export function isShopReturnTarget(itemId: string) {
  if (typeof window === "undefined") return false;
  // The address bar still shows the product page while the shop renders, so match on the item alone.
  return readSaved()?.itemId === itemId;
}

/** Returns `remember(itemId)`; back on the same URL, that tile sits where it was, or a remembered category heading sits at the top. */
export function useShopScrollRestore(containerRef, ready) {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] !== containerRef || $[1] !== ready) {
    t0 = () => {
      if (!ready) {
        return;
      }
      const saved = readSaved();
      if (!saved) {
        return;
      }
      if (saved.url !== currentUrl()) {
        clearSaved();
        return;
      }
      const restore = () => {
        const container = containerRef.current;
        if (!container) {
          return;
        }
        if (saved.category) {
          const heading = container.querySelector(`[data-shop-category="${CSS.escape(saved.category)}"]`);
          if (heading) {
            placeAt(heading, scrollPanelBounds(heading).top + 16);
          } else {
            scrollToTop();
          }
          return;
        }
        const tile = `[data-shop-item-id="${CSS.escape(saved.itemId)}"]`;
        const element = container.querySelector(`${tile}[data-shop-placement="category"]`) ?? container.querySelector(tile);
        if (!element) {
          scrollToTop();
          return;
        }
        if (saved.top !== undefined) {
          placeAt(element, saved.top);
          return;
        }
        const panel = scrollPanelBounds(element);
        const height = element.getBoundingClientRect().height;
        placeAt(element, panel.top + Math.max((panel.height - height) / 2, 0));
      };
      restore();
      const timeoutId = window.setTimeout(() => {
        restore();
        clearSaved();
      }, 0);
      return () => window.clearTimeout(timeoutId);
    };
    t1 = [containerRef, ready];
    $[0] = containerRef;
    $[1] = ready;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useLayoutEffect(t0, t1);
  return _temp;
}
function _temp(itemId) {
  const tile_0 = document.querySelector(`[data-shop-item-id="${CSS.escape(itemId)}"][data-shop-placement="category"]`);
  save({
    url: currentUrl(),
    itemId,
    top: tile_0?.getBoundingClientRect().top ?? 0
  });
}